<template>
  <div>
    <v-card class="feature-card" variant="flat">
      <!--
        The payroll + open dead end, seen from the payment side: this store
        bills shoppers later instead of taking a card, and the backend will
        not extend that credit on a store anyone with the link can shop.

        Deliberately OUTSIDE every conditional on this card, and first. The
        payment-method radio below is itself hidden behind "Accept Shopper
        Payments", so a warning placed next to the radio would be invisible
        in exactly the collapsed state an owner is most likely to leave the
        page in. A blocking configuration error has to be visible without
        expanding anything.

        The access mode is NOT editable here, so the message names the page
        that owns it rather than leaving the owner to hunt.
      -->
      <v-alert
        v-if="hasPayrollAccessConflict"
        type="warning"
        variant="tonal"
        density="comfortable"
        class="mb-4"
        data-testid="payroll-access-conflict"
      >
        No one can check out of this store. "Invoice / Payroll Deductions"
        bills shoppers later rather than taking a card, so the store has to
        be restricted to a known group. Open the Store Login page and change
        "Who can shop here" from "Anyone with the link" to email-domain or
        password access -- or pick credit card below.
      </v-alert>
      <!--
        G2 cleanup (PR G2): the duplicate "Payments & Pricing Options"
        subheader is removed. The outer StoreFeatures EditorCard
        already renders a "Payments & Pricing" title, so the inner
        subheader was redundant. The opening <v-divider /> above the
        first switch row is also dropped since there's no longer a
        header to separate from the row.
      -->
      <v-row align="center">
        <!--
          Display Storefront Pricing Switch.

          PR B4: this row is hidden when StoreFeatures renders its own
          master "Show prices to shoppers" EditorCard at the top of the
          Pricing & Checkout section. The data + watcher + getData()
          stay intact so the save chain is unchanged; the parent calls
          setStorefrontPricing(val) to keep our local
          campaignStorefrontPricingEnabled in sync.
        -->
        <template v-if="!hideStorefrontPricingRow">
          <v-col cols="12">
            <editor-switch-row label="Display Sales Price on Storefront">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-switch
                    v-bind="props"
                    v-model="campaignStorefrontPricingEnabled"
                    @update:modelValue="changeStorefrontPricing"
                    data-testid="ct-experience-pricing"
                    density="compact"
                    hide-details
                    :disabled="disableFields || $store.getters.getIsNewOrderAdded"
                  />
                </template>
                <span>
                  Allow shoppers to see pricing on storefront. Otherwise, only the
                  client will see prices.
                </span>
              </v-tooltip>
            </editor-switch-row>
          </v-col>
          <!-- divider -->
          <v-col class="feature-divider-flex" cols="12">
            <v-divider />
          </v-col>
        </template>
        <!-- Offer Shopper Payments Switch -->
        <v-col cols="12">
          <editor-switch-row label="Accept Shopper Payments">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-switch
                  v-bind="props"
                  v-model="campaignCompanyAllowanceEnabled"
                  @update:modelValue="changeCampaignCompanyAllowanceEnabled"
                  data-testid="ct-experience-allowance"
                  density="compact"
                  hide-details
                  :disabled="
                    !campaignStorefrontPricingEnabled ||
                      disableFields ||
                      $store.getters.getIsNewOrderAdded
                  "
                />
              </template>
              <span v-if="disableFields || $store.getters.getIsNewOrderAdded">
                Payment settings are locked after orders have been placed.
              </span>

              <span v-else>{{ shopperPaymentTooltip }}</span>
            </v-tooltip>
          </editor-switch-row>
        </v-col>
        <!-- divider -->
        <v-col
          class="feature-divider-flex"
          cols="12"
          v-if="campaignCompanyAllowanceEnabled"
        >
          <v-divider />
        </v-col>
        <!-- Select Payment Method -->
        <v-col
          cols="12"
          v-if="campaignCompanyAllowanceEnabled"
          class="feature-drawer pa-4"
        >
          <v-row>
            <v-col cols="12">
              <v-radio-group
                v-model="campaignPaymentMethod"
                @update:modelValue="changePaymentMethod"
                data-testid="ct-experience-pay-method"
                hide-details="auto"
                :disabled="
                  disablePaymentMethod ||
                    !campaignCompanyAllowanceEnabled ||
                    !campaignStorefrontPricingEnabled
                "
                :rules="[validateNotEmpty]"
                required
              >
                <!--
                  PR: payment options rendered side-by-side instead of
                  stacked, so the card uses its full width instead of
                  leaving the right half empty. Both options remain
                  descendants of the v-radio-group (required for
                  v-model) -- only their layout container changed.
                -->
                <div class="d-flex flex-wrap payment-method-options">
                  <div class="payment-method-option">
                    <div class="d-flex align-center flex-wrap">
                      <v-radio
                        value="Individual CC Payment"
                        label="Individual Credit Card Payment"
                        :disabled="!isEnableCreditCardPayment"
                      />
                      <!--
                        PR B4 - DISTRIBUTOR DEFAULT inheritance chip. Pure
                        visual hint sourced from
                        campaign.distributor.default_payment_method. If the
                        column is absent or null, the chip is hidden, so
                        this is forward-compatible with the eventual backend
                        column landing.
                      -->
                      <span
                        v-if="isDistributorDefault('Individual CC Payment')"
                        class="distributor-default-chip"
                        data-testid="distributor-default-chip-cc"
                      >Distributor default</span>
                    </div>
                    <!--
                      The two arms of the CC hint carry distinct testids so
                      a ui-runner scenario can assert WHICH one rendered.
                      The v-else arm is the surface that told a connected
                      distributor they had no Stripe account (2026-09-08);
                      text_absent requires a testid-scoped selector, so
                      without these the regression is not expressible.
                    -->
                    <p
                      v-if="isEnableCreditCardPayment"
                      class="radio-hint"
                      data-testid="ct-experience-cc-hint"
                    >
                      {{ paymentMethodHints["Individual CC Payment"] }}
                    </p>
                    <p
                      v-else
                      class="radio-hint"
                      data-testid="ct-experience-cc-unavailable-hint"
                    >
                      {{ ccPaymentTooltipText }}
                    </p>
                  </div>

                  <div class="payment-method-option">
                    <div class="d-flex align-center flex-wrap">
                      <v-radio
                        value="Invoice / Payroll Deductions"
                        label="Invoice / Payroll Deductions"
                      />
                      <span
                        v-if="isDistributorDefault('Invoice / Payroll Deductions')"
                        class="distributor-default-chip"
                        data-testid="distributor-default-chip-invoice"
                      >Distributor default</span>
                    </div>
                    <p
                      class="radio-hint"
                      data-testid="ct-experience-invoice-hint"
                    >
                      {{ paymentMethodHints["Invoice / Payroll Deductions"] }}
                    </p>
                  </div>
                </div>
              </v-radio-group>
            </v-col>
          </v-row>
          <!-- Tax/fee switches in their own v-row, fully outside the
               radio-group's v-col. Vuetify 3's v-radio-group uses
               provide/inject and intercepts child input events even
               outside the closing tag if they share a DOM ancestor.
               Moving to a separate row prevents this. (#333, #626) -->
          <!--
            Bug Intake #516: the payment method does NOT decide tax posture.
            Sales tax follows the store's nexus and the order's ship-to, on a
            payroll / invoice store exactly as on a card store, so this switch
            is offered for every payment method (tax_exempt is the separate
            off switch for an exempt buyer). The processing fee and the
            consolidate switch below are a property of the CARD rail and stay
            card-only.
          -->
          <v-row>
            <v-col cols="12" class="tax-fee-options">
              <editor-switch-row label="Collect Sales Tax">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                  <v-switch
                    v-bind="props"
                    v-model="campaignCollectSalesTaxEnabled"
                    @update:modelValue="changeTaxOption()"
                    data-testid="ct-experience-sales-tax"
                    :disabled="disableFields"
                    density="compact"
                    hide-details
                  />
                  </template>
                  <span>Collect sales tax from the shopper</span>
                </v-tooltip>
              </editor-switch-row>
              <editor-switch-row
                v-if="campaignPaymentMethod == 'Individual CC Payment'"
                label="Collect Processing Fee"
              >
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                  <v-switch
                    v-bind="props"
                    v-model="campaignCollectProcessingFeeEnabled"
                    @update:modelValue="changeTaxOption()"
                    data-testid="ct-experience-shoper-precessing"
                    :disabled="disableFields"
                    density="compact"
                    hide-details
                  />
                  </template>
                  <span>Pass credit card processing fees (2.9% + 30 ¢) to the shopper</span>
                </v-tooltip>
              </editor-switch-row>
              <!-- The row itself is conditional: an always-rendered empty
                   flex row left a switch-sized gap under the two toggles
                   above whenever consolidation was unavailable. -->
              <editor-switch-row
                v-if="
                  campaignPaymentMethod == 'Individual CC Payment' &&
                    campaignCollectSalesTaxEnabled &&
                    campaignCollectProcessingFeeEnabled
                "
                label="Consolidate Tax &amp; Fee"
              >
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                  <v-switch
                    v-bind="props"
                    v-model="campaignConsolidateTaxFeeEnabled"
                    data-testid="ct-experience-shoper-consolidate"
                    :disabled="disableFields"
                    :key="campaignConsolidateTaxFeeEnabledKey"
                    density="compact"
                    hide-details
                  />
                  </template>
                  <span>Consolidate Tax &amp; Fee on the checkout page</span>
                </v-tooltip>
              </editor-switch-row>
            </v-col>
          </v-row>
        </v-col>
        <!-- divider -->
        <v-col class="feature-divider-flex" cols="12">
          <v-divider />
        </v-col>
        <!--
          Net Checkout (Jake, 2026-10-23, card #492). An explicit store
          setting: it used to turn on as a side effect of choosing a
          password or email-domain Store Login mode. Usable on every Store
          Login mode (MVP-6 of the Net Checkout PIN proposal): what Net
          needs is a Net Checkout PIN, which the PIN row below holds the
          switch for. The server enforces the same rule
          (permits_net_checkout).
        -->
        <v-col cols="12">
          <editor-switch-row label="Net Checkout">
            <template #hint>
              <div
                class="text-caption text-medium-emphasis"
                data-testid="ct-experience-net-checkout-hint"
              >
                <template v-if="netCheckoutEnabled">
                  <span data-testid="ct-experience-net-checkout-on">On:</span>
                  shoppers can place the order now and be invoiced later,
                  instead of paying by card at checkout.
                </template>
                <template v-else>
                  Off: shoppers pay at checkout. Turn on to let them place the
                  order now and be invoiced later.
                </template>
              </div>
            </template>
            <v-switch
              v-model="netCheckoutEnabled"
              @update:modelValue="onNetCheckoutToggle"
              data-testid="ct-experience-net-checkout"
              density="compact"
              hide-details
            />
          </editor-switch-row>
        </v-col>
        <!-- divider -->
        <v-col class="feature-divider-flex" cols="12">
          <v-divider />
        </v-col>
        <!--
          Net credential choice (P1-1 of the Net Checkout PIN proposal). On a
          Shared password store the owner may let the store password itself
          authorize Net Checkout instead of a separate PIN. Offered only on a
          Shared password store, or on one already in that mode (so the owner
          can leave it). Writes go through netPinService (PUT
          net-credential-mode), never the store save; the server owns the
          rule (store_access_rules) and the terms check.
        -->
        <v-col
          v-if="netCredentialModeOffered"
          cols="12"
          data-testid="net-credential-mode-section"
        >
          <v-alert
            v-if="netModeRequiredNotice"
            type="warning"
            variant="tonal"
            density="comfortable"
            class="mb-3"
            data-testid="net-credential-required"
          >
            {{ netModeRequiredNotice }}
          </v-alert>
          <div class="text-subtitle-2 mb-1">How shoppers unlock Net Checkout</div>
          <v-radio-group
            v-model="netCredentialModeDraft"
            :disabled="netModeBusy"
            density="compact"
            hide-details
            data-testid="net-credential-mode"
            @update:modelValue="onNetCredentialModePicked"
          >
            <v-radio
              value="store_password"
              label="Use the store password (no separate PIN)"
              data-testid="net-credential-mode-store-password"
            />
            <v-radio
              value="pin"
              label="Use a separate Net Checkout PIN"
              data-testid="net-credential-mode-pin"
            />
          </v-radio-group>
          <div
            v-if="netCredentialServerMode === 'store_password' && !netModeDirty"
            class="text-body-2 mt-2"
            data-testid="net-credential-mode-status"
          >
            <strong>Net Checkout uses the store password.</strong>
            Shoppers who entered this store's password can choose Net Checkout
            without a PIN. Changing the store password signs them all out.
            While Net Checkout is on, Store Login must stay on Shared password.
          </div>
          <div
            v-if="netCredentialModeDraft === 'store_password' && !netPinStatus.store_password_set"
            class="text-caption text-medium-emphasis mt-1"
            data-testid="net-credential-mode-needs-password"
          >
            This store has no password set yet. Set one under Store Login
            first.
          </div>
          <template v-if="netModeDirty">
            <v-checkbox
              v-if="netCredentialModeDraft === 'store_password'"
              v-model="netModeTermsAccepted"
              density="compact"
              hide-details
              :label="netStorePasswordTermsText"
              data-testid="net-credential-mode-terms"
            />
            <v-alert
              v-if="netModeError"
              type="error"
              variant="tonal"
              density="comfortable"
              class="my-2"
              data-testid="net-credential-mode-error"
            >
              {{ netModeError }}
            </v-alert>
            <div class="d-flex align-center ga-2 mt-2">
              <v-btn
                color="primary"
                variant="flat"
                :disabled="!netModeCanSave"
                :loading="netModeBusy"
                data-testid="net-credential-mode-save"
                @click="saveNetCredentialMode"
              >
                {{
                  netCredentialModeDraft === "store_password"
                    ? "Use the store password"
                    : "Use a PIN"
                }}
              </v-btn>
            </div>
          </template>
        </v-col>
        <!--
          Net Checkout PIN (MVP-7, proposal 3.7). Shown whether or not the
          switch is on: Clear PIN is only allowed while Net is OFF, so the
          row has to be reachable then. The PIN is write-only -- the field
          is always empty on load and the status line says only that one is
          set. Status and writes go through netPinService, not the store
          save, so a PIN is never part of the campaign payload.
        -->
        <v-col
          v-if="netCredentialModeDraft !== 'store_password'"
          cols="12"
          data-testid="net-pin-section"
        >
          <v-alert
            v-if="netPinRequiredNotice"
            type="warning"
            variant="tonal"
            density="comfortable"
            class="mb-3"
            data-testid="net-pin-required"
          >
            {{ netPinRequiredNotice }}
          </v-alert>
          <div class="text-subtitle-2 mb-1">Net Checkout PIN</div>
          <div
            v-if="!campaign || !campaign.id"
            class="text-caption text-medium-emphasis"
            data-testid="net-pin-save-store-first"
          >
            Save the store first, then set its Net Checkout PIN here.
          </div>
          <template v-else>
            <div
              v-if="netPinStatusLoaded"
              class="text-body-2 mb-2"
              data-testid="net-pin-status"
            >
              <strong v-if="netPinStatus.pin_set" data-testid="net-pin-set"
                >A Net Checkout PIN is set<template v-if="netPinSetDate">
                  (changed {{ netPinSetDate }})</template
                >.</strong
              >
              <strong v-else data-testid="net-pin-unset"
                >No Net Checkout PIN is set yet. Shoppers cannot choose Net
                Checkout until you set one.</strong
              >
            </div>
            <div
              v-else-if="netPinLoadFailed"
              class="text-body-2 mb-2"
              data-testid="net-pin-status-error"
            >
              Could not read the Net Checkout PIN status. Reload the page to
              try again.
            </div>
            <div data-testid="net-pin-field-state" :data-empty="netPin ? 'false' : 'true'">
              <v-text-field
                ref="netPinInput"
                v-model="netPin"
                autocomplete="new-password"
                :type="showNetPin ? 'text' : 'password'"
                :label="netPinStatus.pin_set ? 'New Net Checkout PIN' : 'Net Checkout PIN'"
                :hint="netPinPolicyHint"
                persistent-hint
                density="comfortable"
                maxlength="64"
                :disabled="netPinBusy"
                data-testid="net-pin-input"
              >
                <template #append-inner>
                  <v-btn
                    v-if="netPin"
                    icon
                    variant="text"
                    size="small"
                    :aria-label="showNetPin ? 'Hide PIN' : 'Show PIN'"
                    :aria-pressed="showNetPin ? 'true' : 'false'"
                    data-testid="net-pin-visibility"
                    @click="showNetPin = !showNetPin"
                  >
                    <v-icon :icon="showNetPin ? 'visibility_off' : 'visibility'" />
                  </v-btn>
                </template>
              </v-text-field>
            </div>
            <v-checkbox
              v-if="netPinNeedsTerms"
              v-model="netPinTermsAccepted"
              density="compact"
              hide-details
              :label="netTermsText"
              data-testid="net-pin-terms"
            />
            <div
              v-if="netPin && netPinNeedsTerms && !netPinTermsAccepted"
              class="text-caption text-medium-emphasis mb-1"
              data-testid="net-pin-terms-required"
            >
              Please read and accept the Net Checkout terms above to save the
              PIN.
            </div>
            <v-alert
              v-if="netPinError"
              type="error"
              variant="tonal"
              density="comfortable"
              class="my-2"
              data-testid="net-pin-error"
            >
              {{ netPinError }}
            </v-alert>
            <div class="d-flex align-center ga-2 mt-2">
              <v-btn
                color="primary"
                variant="flat"
                :disabled="!netPinCanSave"
                :loading="netPinBusy"
                data-testid="net-pin-save"
                @click="saveNetPin"
              >
                {{ netPinStatus.pin_set ? "Change PIN" : "Set PIN" }}
              </v-btn>
              <v-btn
                v-if="netPinStatus.pin_set"
                variant="outlined"
                :disabled="netPinBusy || netCheckoutEnabled || netPinAutosavePending"
                data-testid="net-pin-clear"
                @click="clearNetPinClicked"
              >
                Clear PIN
              </v-btn>
            </div>
            <div
              v-if="netPinStatus.pin_set && (netCheckoutEnabled || netPinAutosavePending)"
              class="text-caption text-medium-emphasis mt-1"
              data-testid="net-pin-clear-note"
            >
              <template v-if="netCheckoutEnabled">
                Net Checkout is on, so the PIN cannot be cleared. Turn Net
                Checkout off first, or set a new PIN to replace this one.
              </template>
              <template v-else>
                Waiting for your changes to save. Clear PIN is available once
                the store has saved with Net Checkout off.
              </template>
            </div>
            <div
              class="text-caption text-medium-emphasis mt-2"
              data-testid="net-pin-helper"
            >
              Card is still the default. Shoppers who choose Net Checkout are
              asked for this PIN; shoppers paying by card never see it.
              Changing the PIN signs out everyone who entered the old one.
            </div>
          </template>
        </v-col>
        <!-- divider -->
        <v-col class="feature-divider-flex" cols="12">
          <v-divider />
        </v-col>
        <!--
          PR-G follow-up (2026-06-22): the shopper-allowance row +
          drawer + amount input were extracted into the new
          StoreShopperAllowance.vue card, mounted on the Checkout page.
          The legacy schema fields (company_allowance,
          company_allowance_personal_pay) are now owned by that card;
          this component no longer reads or writes them. The
          consolidate-vs-allowance guard watcher below reads
          campaign.company_allowance directly so the conflict dialog
          still fires when both are set on the same campaign.
        -->
        <!--
          Shipping cost switch (restored 2026-10-22; PR #1982 had removed it on
          2026-07-08, which left no way to turn on live-rate shipping). It
          reads AND writes stores.shipping_cost_enabled. When on, checkout
          quotes live rates from the back office. Only the enable switch is
          restored: the legacy flat_rate / flat_rate_range / cart_percentage
          sub-options (shipping_cost_type / shipping_cost_info) are NOT
          editable here; they stay as stored and StorefrontCart still honors
          them. New stores default to on (Store.__init__).
        -->
        <v-col cols="12">
          <editor-switch-row label="Shipping cost">
            <template #hint>
              <div
                class="text-caption text-medium-emphasis"
                data-testid="ct-experience-shipping-cost-note"
              >
                <template v-if="shippingCostEnabled">
                  Shoppers pay the quoted shipping rate at checkout.
                </template>
                <template v-else>
                  Shoppers are not charged for shipping. You pay shipping on these
                  orders.
                </template>
              </div>
              <div
                v-if="shippingCostEnabled"
                class="text-caption text-medium-emphasis"
                data-testid="ct-experience-shipping-hint"
              >
                <template v-if="shippingOriginMissing">
                  No ship-from address is on file for this store yet, so it cannot
                  launch. Add a shipping address to the client company, or yours on
                  Company.
                </template>
                <template v-else>
                  The store needs a ship-from address (the client company's, or
                  yours on Company); Review &amp; Launch flags it if none is on
                  file.
                </template>
              </div>
            </template>
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
              <v-switch
                v-bind="props"
                v-model="shippingCostEnabled"
                @update:modelValue="onShippingCostToggle"
                data-testid="ct-experience-shipping"
                density="compact"
                hide-details
                :disabled="disableFields || $store.getters.getIsNewOrderAdded"
              >
              </v-switch>
              </template>
              <span v-if="disableFields || $store.getters.getIsNewOrderAdded">
                Editing shipping cost is disabled after orders have been placed.
              </span>
              <span v-else>Charge shoppers for shipping at live rates</span>
            </v-tooltip>
          </editor-switch-row>
        </v-col>
        <!-- divider -->
        <v-col class="feature-divider-flex" cols="12">
          <v-divider />
        </v-col>
      </v-row>
    </v-card>

    <consolidate-tax-and-fee-dialog ref="consolidateTaxFeeDialog" />
  </div>
</template>

<script>
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import ConsolidateTaxAndFeeDialog from "./ConsolidateTaxAndFeeDialog.vue";
import EditorSwitchRow from "./components/EditorSwitchRow.vue";
import { isUnsellablePaymentConfiguration } from "@/utils/campaign";
import { useStoreReadinessStore } from "@/stores/storeReadiness";
import {
  PROBLEMS,
  accessConfigProblems,
  problemMessage,
} from "@/utils/storeAccessRules";
import {
  NET_CREDENTIAL_MODE_PIN,
  NET_CREDENTIAL_MODE_STORE_PASSWORD,
  NET_DISTRIBUTOR_TERMS_TEXT,
  NET_STORE_PASSWORD_TERMS_TEXT,
  NET_PIN_MAX_LENGTH,
  NET_PIN_MIN_LENGTH,
  NET_PIN_SUGGESTED_LENGTH,
  clearNetPin,
  getNetPinStatus,
  netPinErrorMessage,
  setNetCredentialMode,
  setNetPin,
} from "@/services/netPinService";

export default {
  props: {
    campaign: Object,
    editMode: Boolean,
    disableFields: Boolean,
    // Disable flag specific to the payment-method radio. The radio
    // stays locked when the campaign has orders even though most other
    // Shopper Experience toggles do not — payment_method is still read
    // by other code paths and is the conservative default to keep
    // locked here. Other toggles in this component (storefront pricing,
    // shopper allowance, sales tax, processing fee, consolidate
    // tax/fee, shipping cost top-level) use `disableFields`, which is
    // wired to the active-or-complete-only flag from the parent.
    disablePaymentMethod: Boolean,
    campaignPriceLimitEnabled: Boolean,
    couponsEnabled: Boolean,
    /**
     * PR B4: when true, the legacy in-line "Display Sales Price on
     * Storefront" row is hidden because StoreFeatures now renders the
     * master toggle at the top of the Pricing & Checkout section. The
     * data + watcher + getData() in this component still own the
     * canonical value -- the parent calls setStorefrontPricing(val) to
     * keep them in sync.
     */
    hideStorefrontPricingRow: { type: Boolean, default: false },
    /**
     * Allowance v1->v2 migration Slice 3 (2026-07-09): the "Accept Shopper
     * Payments" toggle above the payment-method radio used to read its
     * initial state from the legacy ``company_allowance_personal_pay``
     * string column, which was a redundant proxy for the CC-vs-payroll
     * payment-method choice and is now write-orphaned + retired (no v2
     * replacement toggle -- cap-vs-hybrid is derived from the payment method,
     * enforced via ``is_cc_store``). This boolean carries the v2 allowance
     * state (true when an active per-store allowance policy exists) down from
     * the parent, which fetches it from GET /api/stores/<id>/allowance-policy/
     * -- exactly as the Slice-0 OrderLimits gate sources ``allowanceActive``.
     * The component no longer reads ``company_allowance_personal_pay``.
     */
    allowanceActive: { type: Boolean, default: false },
  },

  mixins: [rules, helpers],

  components: {
    EditorSwitchRow,
    ConsolidateTaxAndFeeDialog,
  },

  data() {
    return {
      campaignStorefrontPricingEnabled: false,
      // ``campaignCompanyAllowanceEnabled`` is the local "Accept Shopper
      // Payments" boolean (label on the row above is "Accept Shopper
      // Payments" -- the variable name is historical from when it gated
      // both the Payment + the Allowance row). PR-G follow-up: the
      // Allowance row was extracted; this flag still gates the
      // Payment-method radio + collect-tax/fee group below.
      campaignCompanyAllowanceEnabled: false,
      campaignPaymentMethod: "",
      // New stores collect sales tax by default (Jake-approved; matches
      // StoreAddEdit's campaign data init and the ORM default on
      // stores.collect_sales_tax). This local is the CANONICAL value that
      // getData() returns, and StoreFeatures.updateCampaign() Object.assigns
      // that slice onto the campaign body -- so a `false` here silently
      // clobbers the parent's `true` on the create payload even when the
      // section was never visited. Initializing to false was the reason
      // newly created stores persisted collect_sales_tax = false despite
      // both "defaults" saying true. An EXISTING store still overwrites this
      // via loadFromCampaign(), so a saved OFF is never force-flipped ON.
      campaignCollectSalesTaxEnabled: true,
      campaignCollectProcessingFeeEnabled: false,
      campaignConsolidateTaxFeeEnabled: false,
      campaignConsolidateTaxFeeEnabledKey: 0,
      salesTax: "flat-rate",
      campaignFlatRate: 0,
      shippingCostEnabled: false,
      // stores.net_checkout_enabled (card #492).
      netCheckoutEnabled: false,
      // The switch was turned on with no PIN: shown on, but NOT sent, so the
      // editor never autosaves a state the server refuses (#313/#343).
      netCheckoutHeld: false,
      // Net Checkout PIN (MVP-7). The PIN itself lives only in ``netPin``
      // while it is typed; status comes from the server and carries no PIN.
      netPin: "",
      showNetPin: false,
      netPinStatus: { pin_set: false, pin_set_at: null, terms_current: false },
      netPinStatusLoaded: false,
      netPinLoadFailed: false,
      netPinTermsAccepted: false,
      // A save answered 409: the stored/constant terms version is stale, so
      // the box must be ticked again whatever the status said.
      netPinTermsStale: false,
      netPinBusy: false,
      netPinError: "",
      netTermsText: NET_DISTRIBUTOR_TERMS_TEXT,
      // Net credential choice (P1-1). The draft follows the server's mode
      // until the owner (or the default-at-enable below) picks one.
      netCredentialModeDraft: NET_CREDENTIAL_MODE_PIN,
      netModeTouched: false,
      // The draft was set by turning Net on, not by the owner: turning Net
      // back off drops it.
      netModeAutoDefaulted: false,
      netModeTermsAccepted: false,
      netModeBusy: false,
      netModeError: "",
      netStorePasswordTermsText: NET_STORE_PASSWORD_TERMS_TEXT,
      // From the saved store's launch readiness; false until known.
      shippingOriginMissing: false,

      paymentMethodList: ["Invoice / Payroll Deductions"],
      paymentMethodHints: {
        "Invoice / Payroll Deductions":
          "Collect $ amount of each shopper orders. Use store reports to bill client or report individual spend for payroll deductions",
        "Individual CC Payment": "Standard credit card payment at checkout",
      },
      ccPaymentTooltipText: "",
    };
  },

  watch: {
    // Net Checkout PIN status (MVP-7): read when the store id is known, and
    // again if the editor switches to another store.
    "campaign.id": {
      handler(id, prev) {
        if (id !== prev) {
          this.netPinStatusLoaded = false;
          this.netPinLoadFailed = false;
          // Nothing typed for one store may be sent to another, and the old
          // store's in-flight save no longer owns the busy flag.
          this.resetNetPinDraft();
          this.netPinBusy = false;
          this.resetNetModeDraft();
          this.netModeBusy = false;
        }
        if (id) this.refreshNetPinStatus();
      },
      immediate: true,
    },

    // Slice 3: the v2 allowance state arrives from the parent's async fetch
    // (GET /api/stores/<id>/allowance-policy/). loadFromCampaign may run
    // before that resolves, so mirror later prop changes into the local
    // "Accept Shopper Payments" enabled state. Only drives the enabled flag
    // up; turning it OFF cascades through the existing
    // campaignCompanyAllowanceEnabled watcher (clears payment-method/tax/fee).
    allowanceActive(val) {
      this.campaignCompanyAllowanceEnabled = !!val;
    },

    campaignStorefrontPricingEnabled(val) {
      if (!val) {
        this.campaignCompanyAllowanceEnabled = false;
      }
      this.$emit("update:storefront-pricing-enabled", val);
    },

    campaignCompanyAllowanceEnabled(val) {
      if (!val) {
        this.salesTax = "location-based";
        this.campaignPaymentMethod = "Invoice / Payroll Deductions";
        // Collect Sales Tax is NOT reset here: shopper payments being off
        // does not change the store's tax posture (Bug Intake #516).
        this.campaignFlatRate = 0;
        this.campaignCollectProcessingFeeEnabled = false;
        this.campaignConsolidateTaxFeeEnabled = false;
        // update Vuex store for preview tab
        this.$store.commit("setCollectProcessingFeeEnabled", false);
        this.$store.commit("setConsolidateTaxFeeEnabled", false);
        this.$store.commit("setSmartShippingEnabled", false);
      }
      this.$emit("update:company-allowance-enabled", val);
    },

    campaignConsolidateTaxFeeEnabled(val) {
      // Allowance v1->v2 (Slice 4): the legacy ``campaign.company_allowance``
      // column was dropped. Whether an allowance is active is now the v2
      // ``allowanceActive`` prop (true when a v2 allowance policy is
      // configured) -- the same source the OrderLimits gate uses. Cannot
      // consolidate the tax/fee line when an allowance is active or coupons
      // are enabled.
      let cannotEnable =
        val && (this.allowanceActive || this.couponsEnabled);
      if (cannotEnable) {
        this.campaignConsolidateTaxFeeEnabled = false;
        this.displayConsolidateTaxFeeDialog();
      }

      this.$store.commit(
        "setConsolidateTaxFeeEnabled",
        this.campaignConsolidateTaxFeeEnabled
      );
      this.$emit("campaign-changed");
      this.$emit(
        "update:consolidate-tax-fee-enabled",
        this.campaignConsolidateTaxFeeEnabled
      );
      if (cannotEnable) {
        this.campaignConsolidateTaxFeeEnabledKey++;
      }
    },
  },

  beforeUnmount() {
    this.resetNetPinDraft();
  },

  async mounted() {
    // Set up payment method list based on Stripe connection. Reads the
    // RESOLVED account (see distributorConnectAccountId), never a single
    // raw column -- keying off the legacy column alone is what dropped
    // "Individual CC Payment" for a connected distributor in production.
    if (this.distributorConnectAccountId) {
      this.paymentMethodList = [
        "Individual CC Payment",
        "Invoice / Payroll Deductions",
      ];
    } else {
      this.paymentMethodList = ["Invoice / Payroll Deductions"];
    }

    // Set CC payment tooltip based on user role
    const roleName = this.$store.state.account?.role?.name;
    if (roleName == "Owner") {
      this.ccPaymentTooltipText =
        "Set up your Stripe account in the 'Get Started' tab to enable Credit Card payments.";
    } else if (roleName == "Sales Executive") {
      this.ccPaymentTooltipText =
        "Contact your distributor manager to connect to stripe and enabled credit card payments.";
    } else {
      this.ccPaymentTooltipText =
        "This distributor does not have a stripe account connected.";
    }
  },

  computed: {
    /**
     * Payroll tender on an ungated store -- a configuration no shopper can
     * ever check out of. The SAME rule the Store Login page applies, from
     * the shared helper, so the two pages cannot disagree.
     *
     * The other half of the conflict (the access mode) is not editable from
     * this page, so the message has to say where to go.
     *
     * Reads ``campaign.payment_method`` and NOT the local
     * ``campaignPaymentMethod``. The loader deliberately falls back to
     * "Invoice / Payroll Deductions" for DISPLAY when the column is unset
     * (the radio needs some selection to render), so the local value reads
     * as payroll on a store that has simply never chosen. Keying the
     * warning off it would fire on every brand-new store and train people
     * to ignore it -- the same distinction the loader itself draws with
     * ``hadPersistedPaymentMethod``.
     */
    hasPayrollAccessConflict() {
      return isUnsellablePaymentConfiguration({
        payment_method: this.campaign?.payment_method,
        access_mode: this.campaign?.access_mode,
      });
    },

    /**
     * Net Checkout PIN status helpers (MVP-7).
     */
    netPinSetDate() {
      const raw = this.netPinStatus && this.netPinStatus.pin_set_at;
      if (!raw) return "";
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
    },

    netPinPolicyHint() {
      return (
        `${NET_PIN_MIN_LENGTH} to ${NET_PIN_MAX_LENGTH} characters; ` +
        `${NET_PIN_SUGGESTED_LENGTH} or more is recommended.`
      );
    },

    /** The acknowledgement is needed the first time and when terms change. */
    netPinNeedsTerms() {
      return (
        this.netPinTermsStale ||
        !(this.netPinStatus.pin_set && this.netPinStatus.terms_current)
      );
    },

    /** The editor's autosave has not landed yet (dirty or saving). */
    netPinAutosavePending() {
      const st = this.$store?.state?.storeEntity?.autoSaveState;
      return st === "dirty" || st === "saving";
    },

    netPinCanSave() {
      const len = this.netPin.length;
      if (this.netPinBusy || !this.netPinStatusLoaded) return false;
      // Without the server's current terms version there is nothing valid
      // to acknowledge.
      if (this.netPinNeedsTerms && !this.netPinStatus.terms_version_current) {
        return false;
      }
      if (len < NET_PIN_MIN_LENGTH || len > NET_PIN_MAX_LENGTH) return false;
      return !this.netPinNeedsTerms || this.netPinTermsAccepted;
    },

    /** The shared rule's own words, shown while a switch-on is being held. */
    netPinRequiredNotice() {
      return this.netCheckoutHeld &&
        this.netCredentialModeDraft !== NET_CREDENTIAL_MODE_STORE_PASSWORD
        ? problemMessage(PROBLEMS.NET_CHECKOUT_NEEDS_CREDENTIAL)
        : "";
    },

    /** The server's saved Net credential mode (P1-1); PIN until known. */
    netCredentialServerMode() {
      return (
        (this.netPinStatus && this.netPinStatus.credential_mode) ||
        NET_CREDENTIAL_MODE_PIN
      );
    },

    netCredentialModeOffered() {
      if (!this.campaign || !this.campaign.id || !this.netPinStatusLoaded) {
        return false;
      }
      return (
        !!this.netPinStatus.password_store ||
        this.netCredentialServerMode === NET_CREDENTIAL_MODE_STORE_PASSWORD
      );
    },

    netModeDirty() {
      return this.netCredentialModeDraft !== this.netCredentialServerMode;
    },

    netModeCanSave() {
      if (this.netModeBusy || !this.netModeDirty) return false;
      if (this.netCredentialModeDraft !== NET_CREDENTIAL_MODE_STORE_PASSWORD) {
        return true;
      }
      return (
        !!this.netPinStatus.store_password_set &&
        !!this.netPinStatus.terms_version_current &&
        this.netModeTermsAccepted
      );
    },

    /**
     * Shown while a switch-on is held for the store-password choice: in that
     * mode the rule that can refuse is "must stay on Shared password", or
     * the choice itself is not saved yet.
     */
    netModeRequiredNotice() {
      if (
        !this.netCheckoutHeld ||
        this.netCredentialModeDraft !== NET_CREDENTIAL_MODE_STORE_PASSWORD
      ) {
        return "";
      }
      if (!this.netModeDirty) {
        return problemMessage(
          PROBLEMS.NET_PASSWORD_CREDENTIAL_NEEDS_PASSWORD_MODE
        );
      }
      return (
        "Net Checkout needs a way for shoppers to unlock it. This store " +
        "uses a Shared password, so shoppers who entered it can use Net " +
        "Checkout with no separate PIN. Accept the terms below and choose " +
        "Use the store password, or pick a PIN instead."
      );
    },

    shopperPaymentTooltip() {
      if (this.campaignStorefrontPricingEnabled) {
        return "Allow individual shopper payments";
      } else {
        return "Activate storefront pricing to collect shopper payments";
      }
    },

    /**
     * The distributor's Stripe Connect account id, or "" when there is
     * none. Browser-side mirror of ``Distributor.charging_account_id``
     * (issue #2675): CANONICAL ``stripe_connect_account_id`` first,
     * LEGACY ``stripe_account_id`` as fallback, and an EMPTY STRING counts
     * as ABSENT on both.
     *
     * The loader (``getDistributorById``, behind /campaign/getCampaignById)
     * now resolves this server-side and emits it under the legacy key, so
     * in practice one column is enough. This resolves both anyway, because
     * relying on the loader is what broke: before that fix the payload
     * carried the raw row, production had canonical set + legacy = "", and
     * every consumer of the legacy key read "no account" -- telling a
     * connected distributor they had no Stripe account and refusing to
     * offer the payment method their store was already configured for.
     *
     * Empty string is the real production value: ``handle_oauth_deauthorize``
     * clears the legacy column by writing "", not NULL.
     */
    distributorConnectAccountId() {
      const distributor = this.campaign?.distributor;
      if (!distributor) {
        return "";
      }
      return (
        distributor.stripe_connect_account_id ||
        distributor.stripe_account_id ||
        ""
      );
    },

    isEnableCreditCardPayment() {
      return !!this.distributorConnectAccountId;
    },

    /**
     * PR B4: distributor's default payment_method, if any. Returns "" when
     * the distributor relationship has not yet loaded or the column is
     * unset. The chip rendering is a pure visual cue; missing data simply
     * hides the chip.
     */
    distributorDefaultPaymentMethod() {
      return this.campaign?.distributor?.default_payment_method || "";
    },
  },

  methods: {
    changeStorefrontPricing() {
      this.$store.commit(
        "setStorefrontPricing",
        this.campaignStorefrontPricingEnabled
      );
      this.$emit("campaign-changed");
    },

    changeCampaignCompanyAllowanceEnabled() {
      // The "Accept Shopper Payments" switch above the radio group still
      // routes through this handler. The Vuex setCompanyAllowance commit
      // and the update:company-allowance emit moved to
      // StoreShopperAllowance (PR-G follow-up); this handler now just
      // dirties the parent so autosave catches the payment-method state.
      this.$emit("campaign-changed");
    },

    changePaymentMethod(val) {
      if (val == "Invoice / Payroll Deductions") {
        // Only the CARD-rail settings are cleared. Sales tax is NOT: the
        // payment method does not decide tax posture (Bug Intake #516).
        // The legacy shopper flat rate is still reset: reports pick their
        // tax mode from ``flat_rate == 0`` and this reset predates #516.
        this.campaignFlatRate = 0;
        this.campaignCollectProcessingFeeEnabled = false;
        this.campaignConsolidateTaxFeeEnabled = false;
        this.$store.commit("setCollectProcessingFeeEnabled", false);
        this.$store.commit("setConsolidateTaxFeeEnabled", false);
      }
      this.$emit("campaign-changed");
    },

    changeTaxOption() {
      if (
        !(
          this.campaignCollectSalesTaxEnabled &&
          this.campaignCollectProcessingFeeEnabled
        )
      ) {
        this.campaignConsolidateTaxFeeEnabled = false;
        this.$store.commit("setConsolidateTaxFeeEnabled", false);
      }
      this.$store.commit(
        "setCollectSalesTaxEnabled",
        this.campaignCollectSalesTaxEnabled
      );
      this.$store.commit(
        "setCollectProcessingFeeEnabled",
        this.campaignCollectProcessingFeeEnabled
      );
      this.$emit("campaign-changed");
    },

    changeShopperFlatRate(val) {
      if (val && this.isNumber(val)) {
        this.campaignFlatRate = parseFloat(val);
        this.$emit("campaign-changed");
      }
    },

    // PR-G follow-up (2026-06-22): toggleShopperAllowance /
    // changeShopperAllowance / afterChangingShopperAllowance removed.
    // The Shopper Allowance UI lives in StoreShopperAllowance.vue on
    // the Checkout page now -- it owns the data + handlers.

    /**
     * Ask the same readiness read Review & Launch uses whether the saved
     * store lacks a ship-from address. Display only (no gate logic here);
     * a failed or unavailable read leaves the plain hint.
     */
    async refreshShippingOrigin(campaign) {
      this.shippingOriginMissing = false;
      if (!campaign || !campaign.id || !this.shippingCostEnabled) return;
      try {
        const data = await useStoreReadinessStore().fetchReadiness(
          campaign.id
        );
        const blockers = (data && data.blockers) || [];
        this.shippingOriginMissing = blockers.some(
          (b) => b && b.code === "shipping_origin_missing"
        );
      } catch (err) {
        this.shippingOriginMissing = false;
      }
    },

    onNetCheckoutToggle() {
      this.netPinError = "";
      this.netModeError = "";
      if (!this.netCheckoutEnabled) {
        this.netCheckoutHeld = false;
        if (this.netModeAutoDefaulted) this.resetNetModeDraft();
        this.$emit("campaign-changed", "net_checkout");
        return;
      }
      // Same rule the server enforces (shared table), judged on the SAVED
      // credential mode; only decided once the status has been read, so a
      // slow read never blocks a store that does have a credential.
      const problems = accessConfigProblems({
        access_mode: this.campaign && this.campaign.access_mode,
        net_checkout_enabled: true,
        has_net_credential: this.netPinStatus.pin_set,
        net_credential_mode: this.netCredentialServerMode,
      });
      if (this.netPinStatusLoaded && !this.onlyNonNetProblems(problems)) {
        this.netCheckoutHeld = true;
        if (
          problems.includes(PROBLEMS.NET_CHECKOUT_NEEDS_CREDENTIAL) &&
          this.netPinStatus.password_store &&
          this.netPinStatus.store_password_set &&
          !this.netModeTouched
        ) {
          // P1-1 default: a Shared password store with no PIN offers the
          // store password first. Nothing is sent until the owner accepts
          // the terms and saves the choice.
          this.netCredentialModeDraft = NET_CREDENTIAL_MODE_STORE_PASSWORD;
          this.netModeTouched = true;
          this.netModeAutoDefaulted = true;
          this.netModeTermsAccepted = false;
          return;
        }
        if (this.netCredentialModeDraft !== NET_CREDENTIAL_MODE_STORE_PASSWORD) {
          this.$nextTick(() => this.$refs.netPinInput?.focus?.());
        }
        return;
      }
      this.netCheckoutHeld = false;
      this.$emit("campaign-changed", "net_checkout");
    },

    onNetCredentialModePicked() {
      this.netModeTouched = true;
      this.netModeAutoDefaulted = false;
      this.netModeTermsAccepted = false;
      this.netModeError = "";
    },

    /** Drop the unsaved credential choice: back to the server's mode. */
    resetNetModeDraft() {
      this.netCredentialModeDraft = this.netCredentialServerMode;
      this.netModeTouched = false;
      this.netModeAutoDefaulted = false;
      this.netModeTermsAccepted = false;
      this.netModeError = "";
    },

    async saveNetCredentialMode() {
      const id = this.campaign && this.campaign.id;
      if (!id || !this.netModeCanSave) return;
      const mode = this.netCredentialModeDraft;
      const version =
        mode === NET_CREDENTIAL_MODE_STORE_PASSWORD
          ? this.netPinStatus.terms_version_current
          : undefined;
      this.netModeBusy = true;
      this.netModeError = "";
      let saved;
      try {
        saved = await setNetCredentialMode(id, mode, version);
      } catch (err) {
        if (!this.campaign || this.campaign.id !== id) return;
        const status = err && err.response && err.response.status;
        this.netModeError = netPinErrorMessage(
          err,
          "The Net Checkout choice could not be saved. Try again."
        );
        if (status === 409) {
          this.netModeTermsAccepted = false;
          await this.refreshNetPinStatus();
          if (!this.campaign || this.campaign.id !== id) return;
        }
        this.netModeBusy = false;
        return;
      }
      if (!this.campaign || this.campaign.id !== id) return;
      if (saved && typeof saved === "object" && saved.credential_mode) {
        this.netPinStatus = saved;
        this.netPinStatusLoaded = true;
        this.netPinLoadFailed = false;
      }
      this.netModeBusy = false;
      this.resetNetModeDraft();
      if (this.netCheckoutHeld) {
        // The switch was waiting on this choice: send it if the shared rule
        // now allows it (switching to PIN with no PIN keeps it held).
        const problems = accessConfigProblems({
          access_mode: this.campaign.access_mode,
          net_checkout_enabled: true,
          has_net_credential: this.netPinStatus.pin_set,
          net_credential_mode: this.netCredentialServerMode,
        });
        if (this.onlyNonNetProblems(problems)) {
          this.netCheckoutHeld = false;
          this.$emit("campaign-changed", "net_checkout");
        }
      }
    },

    /** True when none of the problems are Net credential problems. */
    onlyNonNetProblems(problems) {
      return !problems.some(
        (p) =>
          p === PROBLEMS.NET_CHECKOUT_NEEDS_CREDENTIAL ||
          p === PROBLEMS.NET_PASSWORD_CREDENTIAL_NEEDS_PASSWORD_MODE
      );
    },

    /** Drop everything typed or ticked for the PIN (never kept past need). */
    resetNetPinDraft() {
      this.netPin = "";
      this.showNetPin = false;
      this.netPinTermsAccepted = false;
      this.netPinTermsStale = false;
      this.netPinError = "";
    },

    async refreshNetPinStatus() {
      const id = this.campaign && this.campaign.id;
      if (!id) return false;
      try {
        const status = await getNetPinStatus(id);
        // The editor may have moved to another store while this was in
        // flight; a late answer must not paint over the new store's state.
        if (!this.campaign || this.campaign.id !== id) return false;
        this.netPinStatus = status;
        this.netPinStatusLoaded = true;
        this.netPinLoadFailed = false;
        if (!this.netModeTouched) {
          this.netCredentialModeDraft = this.netCredentialServerMode;
        }
        return true;
      } catch (err) {
        if (!this.campaign || this.campaign.id !== id) return false;
        this.netPinLoadFailed = !this.netPinStatusLoaded;
        return false;
      }
    },

    async saveNetPin() {
      const id = this.campaign && this.campaign.id;
      if (!id || !this.netPinCanSave) return;
      const version = this.netPinNeedsTerms
        ? this.netPinStatus.terms_version_current
        : this.netPinStatus.terms_version;
      this.netPinBusy = true;
      this.netPinError = "";
      try {
        await setNetPin(id, this.netPin, version);
      } catch (err) {
        // The editor may have moved to another store while this was in
        // flight: that store's state is not this failure's to change (the
        // store switch already reset the draft and the busy flag).
        if (!this.campaign || this.campaign.id !== id) return;
        const status = err && err.response && err.response.status;
        this.netPinError = netPinErrorMessage(
          err,
          "The Net Checkout PIN could not be saved. Try again."
        );
        if (status === 429 || status >= 500) {
          // Not something the owner can correct by editing the PIN: do not
          // leave it sitting in the field. 400 and 409 keep it, and so does
          // a failure with NO status (a dropped connection): the request
          // may never have left, the owner retries, and nothing server-side
          // says the PIN was exposed.
          this.netPin = "";
          this.showNetPin = false;
        }
        if (status === 409) {
          // The terms moved on: re-read the current version, and ask for
          // the acknowledgement again against it.
          this.netPinTermsStale = true;
          this.netPinTermsAccepted = false;
          await this.refreshNetPinStatus();
          if (!this.campaign || this.campaign.id !== id) return;
        }
        this.netPinBusy = false;
        return;
      }
      if (!this.campaign || this.campaign.id !== id) return;
      this.resetNetPinDraft();
      this.netPinBusy = false;
      // Show the server's own answer. If the re-read fails, the PUT still
      // succeeded, so say so rather than leave "No PIN set" on screen.
      const reread = await this.refreshNetPinStatus();
      if (!reread && this.campaign && this.campaign.id === id) {
        this.netPinStatus = {
          ...this.netPinStatus,
          pin_set: true,
          pin_set_at: new Date().toISOString(),
          // The previous setter is not who just set it; unknown until a
          // re-read succeeds.
          pin_set_by: null,
          terms_version: version,
          terms_current: true,
        };
        this.netPinStatusLoaded = true;
        this.netPinLoadFailed = false;
      }
      if (this.netCheckoutHeld) {
        // The switch was waiting on this PIN: send it now.
        this.netCheckoutHeld = false;
        this.$emit("campaign-changed", "net_checkout");
      }
    },

    async clearNetPinClicked() {
      const id = this.campaign && this.campaign.id;
      if (!id || this.netCheckoutEnabled || this.netPinAutosavePending) return;
      this.netPinBusy = true;
      this.netPinError = "";
      try {
        await clearNetPin(id);
      } catch (err) {
        this.netPinError = netPinErrorMessage(
          err,
          "The Net Checkout PIN could not be cleared. Try again."
        );
        this.netPinBusy = false;
        return;
      }
      this.netPinBusy = false;
      const reread = await this.refreshNetPinStatus();
      if (!reread) {
        this.netPinStatus = {
          ...this.netPinStatus,
          pin_set: false,
          pin_set_at: null,
          terms_current: false,
        };
      }
    },

    onShippingCostToggle() {
      this.$emit("update:shipping-cost-enabled", this.shippingCostEnabled);
      this.$emit("campaign-changed", "shipping_cost");
    },

    displayConsolidateTaxFeeDialog() {
      this.$refs["consolidateTaxFeeDialog"].open();
    },

    /**
     * Handles the consolidate-conflict event from CampaignCoupons.
     * Opens the consolidate dialog and disables coupons.
     */
    handleConsolidateConflict() {
      this.displayConsolidateTaxFeeDialog();
    },

    /**
     * Returns payment/pricing fields for saving the campaign.
     * @returns {Object} Payment and pricing fields to merge into campaign payload.
     */
    /**
     * Allows the parent orchestrator to override the payment method list
     * (e.g., when loadComponent refreshes Stripe status).
     */
    setPaymentMethodList(list) {
      this.paymentMethodList = list;
    },

    /**
     * PR B4 bridge. Lets the parent's master toggle update this
     * component's internal storefront-pricing state without inverting
     * the save direction (the local `campaignStorefrontPricingEnabled`
     * is still the source for getData()). The watcher already emits
     * `update:storefront-pricing-enabled` and clears dependent flags.
     */
    setStorefrontPricing(val) {
      this.campaignStorefrontPricingEnabled = !!val;
    },

    /**
     * PR B4 helper. Returns true if the given payment_method value
     * matches the distributor's configured default.
     */
    isDistributorDefault(value) {
      return (
        !!this.distributorDefaultPaymentMethod &&
        this.distributorDefaultPaymentMethod === value
      );
    },

    getData() {
      // PR-G follow-up (2026-06-22): company_allowance +
      // company_allowance_personal_pay removed from this slice. They are
      // now owned by StoreShopperAllowance.vue, whose getData() is merged
      // into the campaign payload by the StoreCheckout orchestrator.
      //
      // "Accept Shopper Payments" OFF persists as an EMPTY payment_method.
      // The switch has no column of its own (the retirement doc, Slice 3,
      // deliberately added no v2 replacement toggle) -- it IS the
      // CC-vs-payroll choice, so "no method offered" is how OFF is stored
      // and how loadFromCampaign() reads it back. Without this the OFF
      // state is written as "Invoice / Payroll Deductions" by the
      // campaignCompanyAllowanceEnabled watcher, which is indistinguishable
      // from a genuine payroll store and rehydrates the switch back ON.
      return {
        storefront_pricing: this.campaignStorefrontPricingEnabled,
        payment_method: this.campaignCompanyAllowanceEnabled
          ? this.campaignPaymentMethod
          : "",
        collect_sales_tax: this.campaignCollectSalesTaxEnabled,
        sales_tax: this.salesTax,
        flat_rate: this.campaignFlatRate,
        collect_processing_fee: this.campaignCollectProcessingFeeEnabled,
        consolidate_tax_fee: this.campaignConsolidateTaxFeeEnabled,
        shipping_cost_enabled: this.shippingCostEnabled,
        net_checkout_enabled: this.netCheckoutEnabled && !this.netCheckoutHeld,
      };
    },

    /**
     * A save was refused: show the SAVED payment method and the tax/fee
     * settings that choosing payroll clears in the browser. Without this,
     * a refused switch to payroll left tax and fees cleared on screen, and
     * the next save from any section stored them cleared (Bug Intake #351).
     * Only these fields are touched; nothing else the owner is editing.
     */
    adoptSavedAccess(saved) {
      if (!saved) return;
      if ("payment_method" in saved) {
        const method = saved.payment_method || "";
        this.campaignCompanyAllowanceEnabled =
          !!this.allowanceActive || !!method;
        if (method) this.campaignPaymentMethod = method;
      }
      if ("collect_sales_tax" in saved) {
        this.campaignCollectSalesTaxEnabled = !!saved.collect_sales_tax;
      }
      if ("flat_rate" in saved) {
        this.campaignFlatRate = saved.flat_rate;
      }
      if ("collect_processing_fee" in saved) {
        this.campaignCollectProcessingFeeEnabled = !!saved.collect_processing_fee;
      }
      if ("consolidate_tax_fee" in saved) {
        this.campaignConsolidateTaxFeeEnabled = !!saved.consolidate_tax_fee;
      }
      this.$store.commit(
        "setCollectSalesTaxEnabled",
        this.campaignCollectSalesTaxEnabled
      );
      this.$store.commit(
        "setCollectProcessingFeeEnabled",
        this.campaignCollectProcessingFeeEnabled
      );
      this.$store.commit(
        "setConsolidateTaxFeeEnabled",
        this.campaignConsolidateTaxFeeEnabled
      );
    },

    /**
     * Loads payment/pricing data from a campaign object.
     * @param {Object} campaign - The campaign to load from.
     */
    loadFromCampaign(campaign) {
      this.campaignStorefrontPricingEnabled = campaign.storefront_pricing;
      this.$store.commit(
        "setStorefrontPricing",
        this.campaignStorefrontPricingEnabled
      );

      // Allowance v1->v2 migration Slice 3 (2026-07-09): the "Accept Shopper
      // Payments" enabled state now derives from the v2 allowance policy
      // (``allowanceActive`` prop), NOT the write-orphaned legacy
      // ``company_allowance_personal_pay`` string column (retired: it was a
      // redundant proxy for the CC-vs-payroll choice; no v2 replacement
      // toggle). The enabled boolean is still read here because the
      // payment-method radio group -- which IS the cap-vs-hybrid driver --
      // plus the collect-tax/fee disclosure below gate on it.
      //
      // 2026-09-09: derive from the PERSISTED payment method as well, not
      // ``allowanceActive`` alone. The retirement doc says to source this
      // toggle from "the v2 policy / payment method"; Slice 3 wired only the
      // policy half, so a store with no allowance policy rehydrated the
      // switch OFF no matter what the distributor saved, and a store WITH
      // one rehydrated it ON -- the reported production bug (the switch
      // does not stick). A saved payment method means shopper payments were
      // accepted; an active allowance policy still forces it on, since the
      // radio + tax/fee disclosure it gates must be reachable to configure
      // that allowance's cap-vs-hybrid behaviour.
      this.campaignCompanyAllowanceEnabled =
        !!this.allowanceActive || !!campaign.payment_method;

      this.campaignCollectProcessingFeeEnabled = campaign.collect_processing_fee;
      this.campaignCollectSalesTaxEnabled = campaign.collect_sales_tax;
      this.campaignFlatRate = campaign.flat_rate;
      this.salesTax = campaign.sales_tax;
      this.campaignConsolidateTaxFeeEnabled = campaign.consolidate_tax_fee;
      this.campaignPaymentMethod = campaign.payment_method;
      this.shippingCostEnabled = !!campaign.shipping_cost_enabled;
      this.netCheckoutEnabled = !!campaign.net_checkout_enabled;
      this.netCheckoutHeld = false;
      this.refreshShippingOrigin(campaign);

      // The radio needs a selection to render, so an unset payment_method
      // falls back to payroll deductions for DISPLAY. Remember whether the
      // store actually had one persisted: the tax/fee coupling below must
      // key on a REAL choice, not on this display fallback.
      const hadPersistedPaymentMethod = !!this.campaignPaymentMethod;
      if (!this.campaignPaymentMethod) {
        this.campaignPaymentMethod = "Invoice / Payroll Deductions";
      }
      // Invoiced / payroll-deduction stores never charge the shopper a
      // PROCESSING FEE (no card is charged), so selecting that method clears
      // the fee and its consolidate switch. Sales tax is deliberately NOT
      // cleared: the payment method does not decide tax posture, so a saved
      // Collect Sales Tax survives a load on a payroll store (Bug Intake
      // #516 -- this load path used to rewrite it to false, and the next
      // autosave persisted that). This must NOT fire for a store that
      // simply has not chosen a method yet (the display fallback above is
      // not a choice).
      if (
        hadPersistedPaymentMethod &&
        this.campaignPaymentMethod === "Invoice / Payroll Deductions"
      ) {
        this.campaignFlatRate = 0;
        this.campaignCollectProcessingFeeEnabled = false;
        this.campaignConsolidateTaxFeeEnabled = false;
      }

      this.$store.commit(
        "setCollectSalesTaxEnabled",
        this.campaignCollectSalesTaxEnabled
      );
      this.$store.commit(
        "setCollectProcessingFeeEnabled",
        this.campaignCollectProcessingFeeEnabled
      );
      this.$store.commit(
        "setConsolidateTaxFeeEnabled",
        this.campaignConsolidateTaxFeeEnabled
      );
    },
  },
};
</script>

<style scoped>
.payment-method-options {
  column-gap: 48px;
  row-gap: 8px;
}

.payment-method-option {
  flex: 1 1 320px;
  min-width: 280px;
}

/* Hint under each radio. It used to be pulled into place with
   position:relative + left/bottom offsets, which shifted the box 33px past
   its container so the right edge was clipped ("Use store reports tc").
   A plain left margin keeps it inside the column so it wraps. */
.radio-hint {
  font-size: 12px;
  opacity: 0.7;
  margin: 0 0 0 33px;
  max-width: 450px;
}

/* Vuetify hangs a switch thumb a few px past its track, and v-card clips
   overflow by default, so the thumb of every switch flush with this card's
   right edge was cut off. Nothing in this card needs the clip (flat, no
   border radius to round children into). */
.feature-card {
  overflow: visible;
}

/* The tax/fee sub-options sit in the inset payment-method drawer. Their
   switches must still land on the page's one switch column, so the drawer's
   right inset is handed to the rows as a bleed rather than re-laid-out. */
.tax-fee-options {
  --editor-switch-row-bleed: -4px;
  padding-left: 52px; /* the grid gutter (12px) + a 40px indent */
}

.text-label {
  font-size: 16px;
  opacity: 0.7;
}

/* PR B4 - DISTRIBUTOR DEFAULT inheritance chip */
.distributor-default-chip {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: var(--tf-bg-muted, #f1f1f1);
  color: var(--tf-fg-4, #555);
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  line-height: 1.4;
}
</style>
